import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraOrgChart } from '@mono/ui-components/captains';
import { SpecraLink, SpecraSpinner, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraKeyValueRow, SpecraSectionHeader } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useNavigate } from 'react-router-dom';
import { renderRouterLink } from '../../../../lib/renderRouterLink';
import { registryHref } from '../../registryHref';
import { companyFamilyHref } from './companyIdentity';
import { buildFamilyTree } from './familyTree';
import { useCompanyFamily } from './hooks';
export { companyFamilyHref };
const companyHref = (id)=>registryHref({
        kind: 'COMPANY',
        id
    }) ?? undefined;
function LinkRows({ title, rows }) {
    if (!rows.length) return null;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1",
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                title: title,
                variant: "group",
                headingAs: "h4"
            }),
            rows.map((r)=>/*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                    "data-testid": Testids.registryCompany.familyLinkRow,
                    label: /*#__PURE__*/ _jsx(SpecraLink, {
                        href: companyHref(r.fromId),
                        renderLink: renderRouterLink,
                        children: r.sentence
                    }),
                    value: r.dates
                }, r.id))
        ]
    });
}
/**
 * The family as an ownership tree (parent → subsidiaries, acquisitions, renames,
 * JVs) plus the links that are not ownership: reseller channels and affiliates.
 * Selecting a company opens its page.
 */ export function CompanyFamilyView({ family }) {
    const navigate = useNavigate();
    const tree = buildFamilyTree(family);
    const focus = family.nodes.find((n)=>n.isFocus)?.name ?? 'This company';
    const hasOwnership = tree.nodes.length > 1;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-6",
        "data-testid": Testids.registryCompany.familyView,
        children: [
            hasOwnership ? /*#__PURE__*/ _jsx("div", {
                className: "overflow-x-auto",
                children: /*#__PURE__*/ _jsx(SpecraOrgChart, {
                    label: `${focus} corporate family`,
                    emptyMessage: "No family recorded.",
                    nodes: tree.nodes,
                    selectedId: family.focusCompanyId,
                    onNodeSelect: (id)=>{
                        const href = companyHref(id);
                        if (href) navigate(href);
                    },
                    getNodeTestId: ()=>Testids.registryCompany.familyNode
                })
            }) : /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "meta",
                tone: "muted",
                children: "No parent, subsidiaries, acquisitions or JVs recorded yet."
            }),
            /*#__PURE__*/ _jsx(LinkRows, {
                title: "Channel",
                rows: tree.channel
            }),
            /*#__PURE__*/ _jsx(LinkRows, {
                title: "Other links",
                rows: tree.other
            }),
            family.truncated && /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "meta",
                tone: "muted",
                children: "This family is larger than shown. Open a company further out to see its part."
            })
        ]
    });
}
/** The Family section on the company page, with a link to the full view. */ export function CompanyFamilySection({ companyId }) {
    const family = useCompanyFamily(companyId);
    return /*#__PURE__*/ _jsxs("section", {
        "aria-labelledby": "company-family",
        "data-testid": Testids.registryCompany.familySection,
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                title: "Family",
                headingId: "company-family",
                action: /*#__PURE__*/ _jsx(SpecraLink, {
                    href: companyFamilyHref(companyId),
                    renderLink: renderRouterLink,
                    children: "Open family view →"
                })
            }),
            family.isPending ? /*#__PURE__*/ _jsx(SpecraSpinner, {
                label: "Loading the company family"
            }) : family.isError || !family.data ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "meta",
                tone: "muted",
                children: "The family could not be loaded."
            }) : /*#__PURE__*/ _jsx(CompanyFamilyView, {
                family: family.data
            })
        ]
    });
}
